import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StatusColumn } from '../../components/pedidos/StatusColumn';
import { StatusPedido } from '../../types/pedido';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Pedido } from '../../types/pedido';
import { PedidoModal } from '../../components/pedidos/PedidoModal';
import { pedidoService } from '../../services/api';
import { companyConfigService } from '../../services/companyConfigService';

const statusColumns: { status: StatusPedido | StatusPedido[]; title: string }[] = [
  { status: StatusPedido.RECEBIDO, title: 'Solicitado' },
  { status: StatusPedido.EM_PREPARO, title: 'Em Produção' },
  { status: StatusPedido.EM_ROTA, title: 'Em Rota' },
  { 
    status: [StatusPedido.ENTREGUE, StatusPedido.CANCELADO, StatusPedido.PRONTO], 
    title: 'Finalizados' 
  },
];

export const PedidosPage = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [isTogglingOpen, setIsTogglingOpen] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const carregarPedidos = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const agora = new Date();
      const inicioDia = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), 0, 0, 0, 0);
      const fimDia = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), 23, 59, 59, 999);

      const dados = await pedidoService.listar({
        dataInicio: inicioDia.toISOString(),
        dataFim: fimDia.toISOString(),
      });
      setPedidos(dados);
    } catch (err: any) {
      setError(err instanceof Error ? err : new Error('Não foi possível carregar os pedidos.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void carregarPedidos();
  }, [carregarPedidos]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Só faz polling quando a empresa está aberta para pedidos
    if (isOpen !== true) {
      return;
    }

    let isCancelled = false;

    if (!audioRef.current) {
      audioRef.current = new Audio('/sounds/neworder.mp3');
    }

    const buscarNovosPedidos = async () => {
      try {
        const agora = new Date();
        const inicioDia = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), 0, 0, 0, 0);
        const fimDia = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), 23, 59, 59, 999);

        const dados = await pedidoService.listar({
          dataInicio: inicioDia.toISOString(),
          dataFim: fimDia.toISOString(),
        });

        if (isCancelled) {
          return;
        }

        setPedidos((pedidosAnteriores) => {
          const idsAnteriores = new Set(pedidosAnteriores.map((p) => p.id));
          const novosPedidos = dados.filter(
            (p) => !idsAnteriores.has(p.id) && p.status === StatusPedido.RECEBIDO,
          );

          if (novosPedidos.length > 0 && audioRef.current) {
            audioRef.current
              .play()
              .catch(() => undefined);
          }

          return dados;
        });
      } catch {
        // silencioso para não expor rotas no console
      }
    };

    const intervalId = window.setInterval(() => {
      void buscarNovosPedidos();
    }, 30000);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isOpen]);

  useEffect(() => {
    const carregarConfigEmpresa = async () => {
      try {
        const config = await companyConfigService.getMyConfig();
        setIsOpen(config?.isOpen ?? true);
      } catch {
        // silencioso para não expor rotas no console
      }
    };

    void carregarConfigEmpresa();
  }, []);

  const handlePedidoClick = useCallback((pedido: Pedido) => {
    setSelectedPedido(pedido);
  }, []);

  const handleStatusChange = useCallback((pedidoId: string, novoStatus: StatusPedido) => {
    // Placeholder para futuras integrações ao alterar status via drag and drop
  }, []);

  const handleStatusUpdated = useCallback(() => {
    void carregarPedidos();
  }, [carregarPedidos]);

  const handleCloseModal = useCallback(() => {
    setSelectedPedido(null);
  }, []);

  const handleToggleOpen = async () => {
    try {
      setIsTogglingOpen(true);
      const novoValor = !(isOpen ?? true);
      const updated = await companyConfigService.updateMyConfig({ isOpen: novoValor });
      setIsOpen(updated.isOpen ?? novoValor);
    } catch {
      // silencioso para não expor rotas no console
    } finally {
      setIsTogglingOpen(false);
    }
  };

  // Overlay de loading que aparece sobre o conteúdo
  const renderLoadingOverlay = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1300, // Z-index maior que o conteúdo
        pointerEvents: 'auto', // Permite interação com o conteúdo abaixo
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="subtitle1">Atualizando pedidos...</Typography>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* Overlay de loading */}
      {isLoading && renderLoadingOverlay()}
      
      {/* Conteúdo principal */}
      {/* Banner de erro se houver falha na conexão */}
      {error && (
        <Box 
          sx={{ 
            backgroundColor: 'error.light', 
            color: 'error.contrastText',
            p: 2, 
            mb: 3, 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <ErrorOutlineIcon />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">Não foi possível atualizar os pedidos</Typography>
            <Typography variant="body2">Você está visualizando informações desatualizadas. {error.message}</Typography>
          </Box>
          <Button 
            variant="outlined" 
            size="small" 
            color="inherit"
            onClick={carregarPedidos}
            startIcon={<RefreshIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'Atualizando...' : 'Tentar novamente'}
          </Button>
        </Box>
      )}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Pedidos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gerencie os pedidos dos clientes
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={carregarPedidos}
            startIcon={<RefreshIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'Atualizando...' : 'Atualizar pedidos'}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleToggleOpen}
            disabled={isTogglingOpen || isOpen === null}
            color={isOpen ? 'success' : 'error'}
          >
            {isTogglingOpen
              ? 'Atualizando...'
              : isOpen
              ? 'Aberto para pedidos'
              : 'Fechado para pedidos'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 1,
        width: '100%',
        '& > *': {
          flex: '1 1 0',
          minWidth: { xs: '100%', md: 'calc(25% - 16px)' }, // 4 colunas com gap de 2 (16px)
          maxWidth: { xs: '100%', md: 'calc(25% - 16px)' },
          transition: 'all 0.3s ease',
        },
        '& > *:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        }
      }}>
        {statusColumns.map(({ status, title }) => {
          const filteredPedidos = Array.isArray(status)
            ? pedidos.filter((pedido: Pedido) => status.includes(pedido.status))
            : pedidos.filter((pedido: Pedido) => pedido.status === status);
            
          return (
            <Box key={Array.isArray(status) ? status.join('-') : status}>
              <StatusColumn
                title={title}
                status={Array.isArray(status) ? status[0] : status}
                pedidos={filteredPedidos}
                onPedidoClick={handlePedidoClick}
                onStatusChange={handleStatusChange}
                onStatusUpdated={handleStatusUpdated}
              />
            </Box>
          );
        })}
      </Box>

      {/* Modal de detalhes do pedido */}
      {selectedPedido && (
        <PedidoModal
          isOpen={!!selectedPedido}
          onClose={handleCloseModal}
          pedido={selectedPedido}
          onStatusUpdated={handleStatusUpdated}
        />
      )}
    </Box>
  );
};
